import React from 'react';

export interface AiTunePerformanceMetaShape {
  triggeredPerformanceRules?: unknown;
  performanceSnapshot?: unknown;
  performanceReasoning?: unknown;
  llmUsed?: unknown;
  llmFallback?: unknown;
  llmSkipReason?: unknown;
  llmReasoning?: unknown;
  llmFieldAdjustments?: unknown;
  llmCoinAdjustments?: unknown;
  llmProvider?: unknown;
  historyLearningBlocked?: unknown;
  llmHistoryContextUsed?: unknown;
  llmHistoryContextEventCount?: unknown;
  llmRecentTuneOutcomes?: unknown;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((line): line is string => typeof line === 'string' && line.trim().length > 0);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

/** Strip LLM skip lines from performance reasoning — shown in the LLM panel instead. */
function filterPerformanceReasoningLines(lines: string[]): string[] {
  return lines.filter((line) => {
    const t = line.trim();
    if (t.startsWith('LLM advisor:')) {
      return false;
    }
    if (t === '--- LLM advisor ---') {
      return false;
    }
    if (t.startsWith('LLM field hints applied')) {
      return false;
    }
    if (t === '--- History learning ---') {
      return false;
    }
    if (t.startsWith('History learning:')) {
      return false;
    }
    if (t.startsWith('LLM history context:')) {
      return false;
    }
    return true;
  });
}

function resolveLlmSkipReason(meta: AiTunePerformanceMetaShape, reasoning: string[]): string | null {
  const explicit = meta.llmSkipReason;
  if (typeof explicit === 'string' && explicit.trim().length > 0) {
    return explicit.trim();
  }
  for (const line of reasoning) {
    if (line.trim().startsWith('LLM advisor:')) {
      return line.trim().replace(/^LLM advisor:\s*/i, '');
    }
  }
  return null;
}

interface AiTunePerformanceMetaPanelsProps {
  meta: AiTunePerformanceMetaShape | null | undefined;
  showPerformanceBlock?: boolean;
}

/**
 * Performance + LLM advisor panels for AI Tune History and Pending views.
 * Before/After change table is rendered separately by the parent.
 */
export const AiTunePerformanceMetaPanels: React.FC<AiTunePerformanceMetaPanelsProps> = ({
  meta,
  showPerformanceBlock = true,
}) => {
  if (!meta) {
    return null;
  }

  const rules = asStringArray(meta.triggeredPerformanceRules);
  const snapshot = asRecord(meta.performanceSnapshot);
  const allReasoning = asStringArray(meta.performanceReasoning);
  const performanceReasoning = filterPerformanceReasoningLines(allReasoning);
  const llmReasoning = asStringArray(meta.llmReasoning);
  const llmAdjustments = asRecord(meta.llmFieldAdjustments);
  const llmAdjustmentEntries = llmAdjustments
    ? Object.entries(llmAdjustments).filter(([, v]) => v !== undefined && v !== null)
    : [];
  const llmCoinAdjustments = asRecord(meta.llmCoinAdjustments);
  const llmCoinEntries = llmCoinAdjustments
    ? Object.entries(llmCoinAdjustments).filter(([, v]) => v !== undefined && v !== null)
    : [];
  const historyLearningBlocked = Array.isArray(meta.historyLearningBlocked)
    ? meta.historyLearningBlocked.filter(
        (row): row is Record<string, unknown> =>
          row != null && typeof row === 'object' && !Array.isArray(row)
      )
    : [];

  const llmUsed = meta.llmUsed === true;
  const llmFallback = meta.llmFallback === true;
  const llmSkipReason = resolveLlmSkipReason(meta, allReasoning);
  const llmHistoryContextUsed = meta.llmHistoryContextUsed === true;
  const llmHistoryContextEventCount =
    typeof meta.llmHistoryContextEventCount === 'number' ? meta.llmHistoryContextEventCount : 0;

  const hasPerformanceSnapshot = snapshot != null;
  const hasPerformanceContent =
    rules.length > 0 || hasPerformanceSnapshot || performanceReasoning.length > 0;

  const showLlmPanel =
    llmUsed ||
    llmFallback ||
    llmSkipReason != null ||
    llmReasoning.length > 0 ||
    llmAdjustmentEntries.length > 0 ||
    llmCoinEntries.length > 0;

  const showHistoryLearningPanel = historyLearningBlocked.length > 0;

  if (!showPerformanceBlock && !showLlmPanel && !showHistoryLearningPanel) {
    return null;
  }

  return (
    <>
      {showPerformanceBlock && hasPerformanceContent ? (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            background: '#f0f9ff',
            borderRadius: 6,
            fontSize: 12,
            border: '1px solid #bae6fd',
          }}
        >
          <strong style={{ color: '#0c4a6e' }}>Performance at event</strong>
          {rules.length > 0 ? (
            <p style={{ margin: '6px 0 0', color: '#0369a1' }}>
              Rules: {rules.join(', ')}
            </p>
          ) : null}
          {hasPerformanceSnapshot ? (
            <p style={{ margin: '4px 0 0', color: '#0c4a6e' }}>
              Window net: {String(snapshot.netPnl ?? '—')}
              {' '}({String(snapshot.actualCount ?? '—')} closes)
            </p>
          ) : null}
          {performanceReasoning.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <strong style={{ fontSize: 11, color: '#0369a1' }}>Performance reasoning</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: '#0c4a6e', lineHeight: 1.45 }}>
                {performanceReasoning.map((line, i) => (
                  <li key={`perf-reason-${i}`}>{line}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {showHistoryLearningPanel ? (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            background: '#fffbeb',
            borderRadius: 6,
            fontSize: 12,
            border: '1px solid #fde68a',
          }}
        >
          <strong style={{ color: '#92400e' }}>History learning — blocked repeats</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: '#78350f', lineHeight: 1.45 }}>
            {historyLearningBlocked.map((row, i) => (
              <li key={`hist-block-${i}`}>
                {String(row.label ?? row.field ?? 'Field')}
                {' '}
                (kept {String(row.keptValue ?? '—')}, blocked {String(row.blockedValue ?? '—')})
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {showLlmPanel ? (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            background: '#faf5ff',
            borderRadius: 6,
            fontSize: 12,
            border: '1px solid #e9d5ff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <strong style={{ color: '#5b21b6' }}>LLM advisor</strong>
            {llmUsed ? (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#166534',
                  background: '#dcfce7',
                  padding: '2px 8px',
                  borderRadius: 10,
                }}
              >
                used
              </span>
            ) : llmFallback ? (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#92400e',
                  background: '#fef3c7',
                  padding: '2px 8px',
                  borderRadius: 10,
                }}
              >
                fallback
              </span>
            ) : (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#64748b',
                  background: '#f1f5f9',
                  padding: '2px 8px',
                  borderRadius: 10,
                }}
              >
                not used
              </span>
            )}
            {typeof meta.llmProvider === 'string' && meta.llmProvider.trim().length > 0 ? (
              <span style={{ fontSize: 10, color: '#7c3aed' }}>{meta.llmProvider}</span>
            ) : null}
            {llmHistoryContextUsed ? (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#5b21b6',
                  background: '#ede9fe',
                  padding: '2px 8px',
                  borderRadius: 10,
                }}
              >
                history context ({llmHistoryContextEventCount})
              </span>
            ) : null}
          </div>

          {llmSkipReason && (!llmUsed || llmFallback) ? (
            <p style={{ margin: '8px 0 0', color: llmFallback ? '#92400e' : '#64748b', lineHeight: 1.45 }}>
              {llmSkipReason}
            </p>
          ) : null}

          {llmReasoning.length > 0 ? (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: '#5b21b6', lineHeight: 1.45 }}>
              {llmReasoning.map((line, i) => (
                <li key={`llm-reason-${i}`}>{line}</li>
              ))}
            </ul>
          ) : null}

          {llmAdjustmentEntries.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <strong style={{ fontSize: 11, color: '#6d28d9' }}>LLM field adjustments (incremental only)</strong>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 11,
                  marginTop: 6,
                }}
              >
                <tbody>
                  {llmAdjustmentEntries.map(([field, value]) => (
                    <tr key={field}>
                      <td
                        style={{
                          padding: '4px 8px 4px 0',
                          fontWeight: 600,
                          color: '#5b21b6',
                          verticalAlign: 'top',
                        }}
                      >
                        {field}
                      </td>
                      <td style={{ padding: '4px 0', color: '#4c1d95' }}>{String(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : llmUsed && llmAdjustmentEntries.length === 0 ? (
            <p style={{ margin: '8px 0 0', fontSize: 11, color: '#7c3aed' }}>
              No incremental field changes (rules/regime already covered the diff).
            </p>
          ) : null}

          {llmCoinEntries.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <strong style={{ fontSize: 11, color: '#6d28d9' }}>LLM coin adjustments (server-validated)</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: '#5b21b6', lineHeight: 1.45 }}>
                {llmCoinEntries.map(([key, value]) => (
                  <li key={`llm-coin-${key}`}>
                    {key}: {Array.isArray(value) ? (value as unknown[]).join(', ') : String(value)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default AiTunePerformanceMetaPanels;
